import { runInAction } from 'mobx';
import {
  sendNoLinkedSizeChartEvent,
  sendPresizeLoadedEvent,
  sendSizingAppliedEventForGroups,
  unify2DSeparator,
} from '../../lib/fragment-helper';
import { SizingStore } from './sizing-store';
import { SizingGroup } from '../../models/sizing-group';
import { SizingResult } from '../../models/sizing';
import { addManuallyEditedFlag } from '../../lib/article-utils';
import { ErrorType } from '../../models/response';
import presizeBackendRest from '../../services/presize-backend-rest';
import { UserAbortedError, RestApiError } from '../../lib/rest-api';
import {
  mapRestSizeChartsToSizingResult,
  mapRestSizeChartsWithSeparateDistribution,
} from '../../lib/adapters/size-chart-adapter';
import { RecommendedDistribution, RecommendedSizeChartParams } from '../../models/rest/sizing';
import { mapRestDistributionToStore } from '../../lib/adapters/distribution-adapter';
import { Article } from '../../models/sizing-data';
import { sendEvent } from '../../services/event-tracking';

export class LoadSizingRestAction {
  applyGroupSizing(group: SizingGroup, sizingResult: SizingResult) {
    group.sizing = {
      sizingGroup: group.sizingGroupKey(),
      enableBox: group.enableBox,
      selectedSizing: sizingResult.selectedSizing,
      recommendedSizing: sizingResult.recommendedSizing,
      sizeChartList: sizingResult.sizeChartList,
      referenceBrandCode: group.referenceBrandCode,
      sizingDecisions: sizingResult.sizingDecisions,
      decision: undefined,
      boxRestriction: group.boxRestriction,
    };
    group.selectedSizeChart = sizingResult.sizeChartList.find(
      (sc) => sc.sizeChartId === sizingResult.selectedSizing.sizeChartId
    );
    group.selectedSizeKey = group.selectedSizeChart?.sizeKeys.find(
      (sizeKey) => sizeKey.sizeKeyId === sizingResult.selectedSizing.sizeKeyId
    );
  }

  /**
   * Loads sizing for all groups using REST endpoints.
   * Called when isDbMigrated() === true and groups come from the REST article-groups endpoint.
   *
   * Current: all groups use GET /recommended-size-charts-with-distribution (PT-51850).
   * PT-51851/52 will refine sized groups (sizeList.length > 0) to use parallel chart+distribution calls.
   */
  // No default: the production Babel build shifts defaulted params after `this` by one.
  async loadSizingRest(this: SizingStore, signal?: AbortSignal, isNewOrderCreated?: boolean): Promise<void> {
    // orderCreated is emitted by createOrderAndGroups(); this flag only gates the initial addArticle events.
    if (this.sizingGroups.length === 0) {
      return;
    }

    // updateBoxRestrictions relies on this.boxRestrictions. If the catalog is still empty
    // during initial REST hydration, it treats persisted IDs as deleted and resets them.
    const hasPersistedBoxRestriction = this.sizingGroups.some((group) => !!group.boxRestriction?.boxRestrictionId);
    if (this.sizingGroups.some((group) => group.enableBox) || hasPersistedBoxRestriction) {
      await this.getBoxRestrictions();
    }

    for (const group of this.sizingGroups) {
      await this.reloadSizingForGroup(group, signal, true);
      if (signal?.aborted) return;
      if (isNewOrderCreated && group.sizing && !group.noSizeChart) {
        sendEvent(group, 'addArticle', undefined, this.additionalData());
      }
    }

    if (this.missingCgLabels.length > 0) {
      runInAction(() => (this.noLinkedSizeCharts = true));
      sendNoLinkedSizeChartEvent(this.missingCgLabels);
    }

    this.processReferenceBrandsWithUsage();

    this.sizingGroups.forEach((sizingGroup) => {
      if (!sizingGroup.boxRestriction?.boxRestrictionId) {
        sizingGroup.articles.forEach((article) => {
          addManuallyEditedFlag(article, sizingGroup?.selectedSizeKey?.distribution);
          if (article.manuallyEdited && article.sizing) {
            article.editedQuantities = { ...article.sizing };
          }
        });
      }
    });

    sendSizingAppliedEventForGroups(this.sizingGroups.filter((g) => !g.noSizeChart));

    runInAction(() => (this.presizeLoaded = true));
    sendPresizeLoadedEvent(this.articles);
  }

  async reloadSizingForGroup(
    this: SizingStore,
    group: SizingGroup,
    signal?: AbortSignal,
    initialCalculation = true
  ): Promise<Article[] | undefined> {
    runInAction(() => (group.loadingSizing = true));
    try {
      const result = await this.loadSizingForGroup(group, signal);
      if (result) {
        this.applyGroupSizing(group, result.sizingResult);
        if (result.distribution && group.selectedSizeKey && group.selectedSizeChart) {
          const mapped = mapRestDistributionToStore(result.distribution);
          if (mapped) {
            group.selectedSizeKey.distribution = mapped.distribution;
            group.selectedSizeKey.distributionType = mapped.distributionType;
            group.selectedSizeKey.recommendationStrategy = mapped.recoStrategy;
            group.selectedSizeKey.recommendationLevel = mapped.recoLevel;
            group.recommendedDistribution = {
              ...mapped,
              sizeChartCode: group.selectedSizeChart.sizeChartCode,
              lengthChartCode: group.selectedSizeChart.lengthChartCode ?? null,
              sizeKeyId: group.selectedSizeKey.sizeKeyId,
              referenceBrandCode: group.referenceBrandCode ?? null,
            };
          }
        }
        if (
          !result.distribution &&
          group.selectedSizeKey?.distribution &&
          group.selectedSizeKey?.distributionType &&
          group.selectedSizeChart
        ) {
          group.recommendedDistribution = {
            distribution: group.selectedSizeKey.distribution,
            distributionType: group.selectedSizeKey.distributionType,
            recoStrategy: group.selectedSizeKey.recommendationStrategy ?? '',
            recoLevel: group.selectedSizeKey.recommendationLevel ?? '',
            sizeChartCode: group.selectedSizeChart.sizeChartCode,
            lengthChartCode: group.selectedSizeChart.lengthChartCode ?? null,
            sizeKeyId: group.selectedSizeKey.sizeKeyId,
            referenceBrandCode: group.referenceBrandCode ?? null,
          };
        }
        unify2DSeparator(group);
        await this.updateBoxRestrictions(group);
        const changedArticles = await this.calculateQuantities(initialCalculation, group);
        if (signal?.aborted) return;
        return changedArticles;
      }
    } catch (error) {
      if (error instanceof UserAbortedError) return;
      if (error === ErrorType.NO_LINKED_SIZE_CHART || (error instanceof RestApiError && error.status === 400)) {
        runInAction(() => {
          group.noSizeChart = true;
          this.missingCgLabels.push(`${group.commodityGroup.names[0]} - ${group.commodityGroup.names[1]}`);
        });
      } else {
        throw error;
      }
    } finally {
      runInAction(() => (group.loadingSizing = false));
    }
    return undefined;
  }

  private async loadSizingForGroup(
    this: SizingStore,
    group: SizingGroup,
    signal?: AbortSignal
  ): Promise<{ sizingResult: SizingResult; distribution?: RecommendedDistribution } | null> {
    const params = {
      cg_code: group.commodityGroupCode,
      brand_code: this.brandCode,
      season_code: this.seasonCode,
      size_structure: group.sizeStructure,
      silo: group.silo ?? null,
    };

    const chartParams: RecommendedSizeChartParams = {
      ...params,
      reference_brand_code: group.referenceBrandCode ?? null,
    };

    // PT-51851: sized groups (previously sized in BHOS) fetch charts + distribution in parallel.
    // group.sizeChartId stores the size_chart_code from the saved group.
    if (group.sizeList.length > 0 && group.sizeChartId) {
      const sizeKeys = group.sizeList.join(';');
      const [chartsResponse, distribution] = await Promise.all([
        presizeBackendRest.getSizeCharts(chartParams, signal),
        presizeBackendRest.getSizeDistribution(
          {
            ...params,
            size_chart_code: group.sizeChartId,
            size_keys: sizeKeys,
            reference_brand_code: group.referenceBrandCode ?? null,
          },
          signal
        ),
      ]);
      return {
        sizingResult: mapRestSizeChartsWithSeparateDistribution(
          chartsResponse,
          distribution,
          group.id,
          group.sizeChartId,
          sizeKeys
        ),
        distribution,
      };
    }

    // PT-51850: unsized groups, or sized groups without a sizeChartId, use the single combined endpoint.
    const response = await presizeBackendRest.getSizeChartsWithDistribution(chartParams, signal);
    return { sizingResult: mapRestSizeChartsToSizingResult(response, group.id) };
  }
}
